import { RoomServiceClient, TokenVerifier } from 'livekit-server-sdk'

import { RoomLookupErrorKind } from '@/src/lib/roomLookupError'

interface IGetCheckMeetData {
  data: {
    company_id: number
    meet_id: number
    company_code?: string
    meet_code?: string
    client_domain?: string | null
  }
  status: boolean
}

export interface CheckMeetResult {
  company_code: string
  meet_code: string
  client_domain: number
  data: IGetCheckMeetData | null
  /**
   * Почему check_meet не дал встречу.
   *
   * `null` — данные пришли и `status` истинный.
   * `not_found` — платформа ответила, что встречи нет.
   * `failed` — сеть, таймаут или 5xx.
   */
  lookupError: RoomLookupErrorKind | null
}

export function getRoomClient(livekit_url: string): RoomServiceClient {
  checkKeys()
  return new RoomServiceClient(livekit_url)
}

function checkKeys() {
  if (typeof process.env.LIVEKIT_API_KEY === 'undefined') {
    throw new Error('LIVEKIT_API_KEY is not defined')
  }

  if (typeof process.env.LIVEKIT_API_SECRET === 'undefined') {
    throw new Error('LIVEKIT_API_SECRET is not defined')
  }
}

export function getTokenVerifier(): TokenVerifier {
  checkKeys()

  return new TokenVerifier(process.env.LIVEKIT_API_KEY, process.env.LIVEKIT_API_SECRET)
}

// Helper функция для retry логики
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 3,
  delay: number = 1000,
): Promise<Response | null> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Attempt ${attempt}/${maxRetries} for ${url}`)
      const response = await fetch(url, options)

      // Если запрос успешный, возвращаем ответ
      if (response.ok) {
        return response
      }

      // Если это не последняя попытка и ошибка сервера (5xx), пробуем еще раз
      if (attempt < maxRetries && response.status >= 500) {
        console.log(`Server error (${response.status}), retrying in ${delay}ms...`)
        await new Promise((resolve) => setTimeout(resolve, delay))
        delay *= 2 // Увеличиваем задержку экспоненциально
        continue
      }

      // Для клиентских ошибок (4xx) не делаем retry
      return response
    } catch (error) {
      if (attempt === maxRetries) {
        throw error
      }

      // Если это не AbortError, пробуем еще раз
      if (error.name !== 'AbortError') {
        console.log(`Network error on attempt ${attempt}, retrying in ${delay}ms...`, error.message)
        await new Promise((resolve) => setTimeout(resolve, delay))
        delay *= 2
      } else {
        throw error // AbortError не ретраим
      }
    }
  }

  return null
}

export async function getCheckMeet(host: string, pathname: string) {
  let company_code: string, meet_code: string, client_domain: number

  if (pathname.slice(1).split('/').length === 2) {
    ;[company_code, meet_code] = pathname.slice(1).split('/')
    client_domain = 0
  } else {
    company_code = host
    meet_code = pathname.slice(1).split('/')[0]
    client_domain = 1
  }

  let data: IGetCheckMeetData = null
  let lookupError: RoomLookupErrorKind | null = null

  const controller = new AbortController()
  // Увеличиваем timeout до 15 секунд для учета retry
  const timeoutId = setTimeout(() => controller.abort(), 15000)

  try {
    const url = `${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/check_meet/${meet_code}/${company_code}?client_domain=${client_domain}`
    console.log('Fetching meet data from:', url)

    const res = await fetchWithRetry(
      url,
      {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
      3,
      1000,
    ) // 3 попытки с начальной задержкой 1 секунда

    clearTimeout(timeoutId)

    if (res && typeof res.json === 'function' && res.ok) {
      data = await res.json()
      console.log('Meet data received successfully:', data)

      if (!data?.status) {
        lookupError = 'not_found'
      }
    } else if (res) {
      console.error('API returned error status:', res.status, res.statusText)
      lookupError = res.status >= 500 ? 'failed' : 'not_found'

      try {
        const errorData = await res.json()
        console.error('Error response:', errorData)
      } catch (jsonError) {
        console.error('Failed to parse error response:', jsonError)
      }
    } else {
      console.error('Invalid response object or fetch failed')
      lookupError = 'failed'
    }
  } catch (e) {
    clearTimeout(timeoutId)
    lookupError = 'failed'

    if (e.name === 'AbortError') {
      console.error(
        'Request was aborted due to timeout (15s). The API server may be slow or unavailable.',
      )
    } else {
      console.error('Error with checking meet after retries:', e)
    }
  }

  return {
    company_code,
    meet_code,
    client_domain,
    data,
    lookupError,
  }
}
